import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import LatestComment from '../../components/Contracts/LatestComment';
import Footer from '../../components/Footer/Footer';
import StatusLabel from '../../components/Status/StatusLabel';
import { CONTRACT_STATUS_OPTIONS, contractStatusLabel, displayContractStatus } from '../../utils/contractStatus';

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${String(date.getDate()).padStart(2, '0')}, ${date.getFullYear()}`;
};

const getContractStatusChip = (contract) => {
  const status = displayContractStatus(contract);
  return <StatusLabel kind="contract" status={status} label={contractStatusLabel(status)} size="small" />;
};

const DepartmentLeadDashboard = () => {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  const [pagination, setPagination] = useState({ total: 0 });
  const [statusFilter, setStatusFilter] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');
  // A department head lists contracts without opening them, so the termination
  // recommendation, which lived on the contract page, is made from the row.
  const [recommendFor, setRecommendFor] = useState(null);
  const [recommendReason, setRecommendReason] = useState('');
  const [recommending, setRecommending] = useState(false);

  useEffect(() => {
    fetchContracts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, page, statusFilter, sortOrder]);

  const fetchContracts = async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        search,
        sortOrder,
      };
      if (statusFilter) params.status = statusFilter;
      const response = await api.get('/contracts', { params });

      if (response.data.success) {
        setContracts(response.data.data || []);
        setPagination(response.data.pagination || { total: 0 });
      } else {
        setContracts([]);
        setPagination({ total: 0 });
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to fetch department contracts');
      setContracts([]);
      setPagination({ total: 0 });
    } finally {
      setLoading(false);
    }
  };

  const closeRecommend = () => {
    setRecommendFor(null);
    setRecommendReason('');
  };

  const handleRecommendTermination = async () => {
    if (!recommendReason.trim()) {
      toast.error('Please provide a recommendation reason');
      return;
    }
    setRecommending(true);
    try {
      await api.post(`/contracts/${recommendFor._id}/recommend-termination`, { reason: recommendReason.trim() });
      toast.success('Termination recommendation sent to legal team');
      closeRecommend();
      fetchContracts();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send termination recommendation');
    } finally {
      setRecommending(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#fff', display: 'flex', flexDirection: 'column' }}>
      <Container maxWidth="lg" sx={{ pt: { xs: 3, sm: 5 }, pb: { xs: 3, sm: 4 }, px: { xs: 2, sm: 3 }, flex: 1 }}>
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontWeight: 700, color: '#111827', fontSize: { xs: '34px', sm: '32px' }, lineHeight: 1.15 }}>
            Supplier List
          </Typography>
          <Typography sx={{ color: '#6b7280', fontSize: '14px' }}>
            View your department suppliers and their contract status
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, mb: 2, gap: 1.5, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search"
            size="small"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#9ca3af', fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              flex: 1,
              minWidth: { xs: '100%', sm: '180px' },
              maxWidth: { xs: '100%', sm: '280px' },
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                '& input': { fontSize: '14px' },
                '& fieldset': { borderColor: '#e5e7eb' },
              },
            }}
          />
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <Select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
              displayEmpty
              sx={{ borderRadius: '8px', fontSize: '14px', backgroundColor: '#fff' }}
            >
              <MenuItem value="">All Statuses</MenuItem>
              {CONTRACT_STATUS_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title={sortOrder === 'asc' ? 'Oldest first — click for newest first' : 'Newest first — click for oldest first'}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => { setSortOrder(s => s === 'asc' ? 'desc' : 'asc'); setPage(0); }}
              startIcon={sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
              sx={{
                borderColor: '#e5e7eb',
                color: '#111827',
                textTransform: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                whiteSpace: 'nowrap',
                '&:hover': { borderColor: '#d1d5db', bgcolor: '#f9fafb' }
              }}
            >
              {sortOrder === 'asc' ? 'Oldest first' : 'Newest first'}
            </Button>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon fontSize="small" />}
            sx={{
              borderColor: '#e5e7eb',
              color: '#111827',
              textTransform: 'none',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: 500,
            }}
          >
            Export
          </Button>
        </Box>

        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
          <Table sx={{ minWidth: 760 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#fff' }}>
                <TableCell sx={{ fontSize: '13px', color: '#6b7280', fontWeight: 500 }}>Vendor Number</TableCell>
                <TableCell sx={{ fontSize: '13px', color: '#6b7280', fontWeight: 500 }}>Company Name</TableCell>
                <TableCell sx={{ fontSize: '13px', color: '#6b7280', fontWeight: 500 }}>Contract Expiry Date</TableCell>
                <TableCell sx={{ fontSize: '13px', color: '#6b7280', fontWeight: 500 }}>Last Updated</TableCell>
                <TableCell sx={{ fontSize: '13px', color: '#6b7280', fontWeight: 500 }}>Contract Status</TableCell>
                <TableCell sx={{ fontSize: '13px', color: '#6b7280', fontWeight: 500 }}>Latest Comment</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 5 }}><CircularProgress /></TableCell>
                </TableRow>
              ) : contracts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                    <Typography sx={{ color: '#6b7280', fontSize: '14px' }}>No contracts found for your department</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                contracts.map((contract) => (
                  <TableRow key={contract._id} hover sx={{ '&:hover': { backgroundColor: '#f9fafb' } }}>
                    <TableCell sx={{ fontSize: '14px', color: '#111827' }}>{contract.supplier?.vendorNumber || '-'}</TableCell>
                    <TableCell sx={{ fontSize: '14px', color: '#111827' }}>{contract.supplier?.supplierName || '-'}</TableCell>
                    <TableCell sx={{ fontSize: '14px', color: '#111827' }}>{formatDate(contract.endDate)}</TableCell>
                    <TableCell sx={{ fontSize: '14px', color: '#111827' }}>{formatDate(contract.updatedAt || contract.createdAt)}</TableCell>
                    <TableCell>{getContractStatusChip(contract)}</TableCell>
                    <TableCell><LatestComment comment={contract.latestComment} /></TableCell>
                    <TableCell align="right">
                      {contract.status === 'active' && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setRecommendFor(contract)}
                          sx={{ textTransform: 'none', borderColor: '#fca5a5', color: '#b91c1c', fontSize: '13px', whiteSpace: 'nowrap', '&:hover': { borderColor: '#dc2626', bgcolor: '#fef2f2' } }}
                        >
                          Recommend Termination
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {!loading && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
              <Typography sx={{ color: '#6b7280', fontSize: '13px' }}>
                0 of {pagination.total || contracts.length} row(s) selected.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={page === 0}
                  onClick={() => setPage((prev) => Math.max(0, prev - 1))}
                  sx={{ textTransform: 'none', borderColor: '#e5e7eb', color: '#374151', fontSize: '13px' }}
                >
                  Previous
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={contracts.length < rowsPerPage}
                  onClick={() => setPage((prev) => prev + 1)}
                  sx={{ textTransform: 'none', borderColor: '#e5e7eb', color: '#374151', fontSize: '13px' }}
                >
                  Next
                </Button>
              </Box>
            </Box>
          )}
        </TableContainer>
      </Container>
      <Dialog open={Boolean(recommendFor)} onClose={closeRecommend} maxWidth="sm" fullWidth>
        <DialogTitle>Recommend Contract Termination</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {recommendFor?.supplier?.supplierName || 'This contract'}: legal will be notified and will actualize the termination.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Reason"
            value={recommendReason}
            onChange={(e) => setRecommendReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRecommend} disabled={recommending} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRecommendTermination}
            disabled={recommending || !recommendReason.trim()}
            sx={{ textTransform: 'none' }}
          >
            {recommending ? 'Sending...' : 'Recommend Termination'}
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </Box>
  );
};

export default DepartmentLeadDashboard;
